import { Injectable, inject } from '@angular/core';
import { Router } from '@angular/router';
import { OidcSecurityService } from 'angular-auth-oidc-client';
import { UnauthorizedError } from '@core/errors/http-errors';

/**
 * Centralizes the 401 → logout flow so it can be reached by both the HTTP
 * interceptor (`errorMappingInterceptor`) and the fetch-based SSE path
 * (`SseService`), which bypasses interceptors entirely.
 *
 * The `isHandling` guard debounces concurrent 401s so we only trigger a single
 * logoff/redirect even when several requests fail at once.
 */
@Injectable({ providedIn: 'root' })
export class UnauthorizedHandlerService {
  private readonly oidcSecurityService = inject(OidcSecurityService);
  private readonly router = inject(Router);
  private isHandling = false;

  /**
   * Triggers the logout flow (once) and always throws `UnauthorizedError` so the
   * caller can distinguish a logout redirect from other failures.
   */
  handle(): never {
    if (!this.isHandling) {
      this.isHandling = true;

      // Store access-denied message to show after logout redirect
      sessionStorage.setItem('postLogoutMessage', 'access-denied');

      // Perform full logout to clear identity server session.
      // This redirects to identity server logout, then back to
      // postLogoutRedirectUri (/login).
      this.oidcSecurityService.logoff('portal-auth-config').subscribe({
        error: () => {
          // If logout fails, still try to redirect
          this.router
            .navigate(['/login'], {
              queryParams: { message: 'access-denied' },
            })
            .finally(() => {
              this.isHandling = false;
            });
        },
      });
    }

    throw new UnauthorizedError();
  }
}
